/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState } from 'react';
import { Modal, Input, Button, Message } from '@arco-design/web-react';
import { useDispatch } from 'react-redux';
import { login } from '@/store/slices/auth';
import { isMobile } from '@/utils/utils';
import styles from './index.module.less';

interface LoginModalProps {
  visible: boolean;
  onClose: () => void;
}

function LoginModal({ visible, onClose }: LoginModalProps) {
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setUsername('');
    setPassword('');
    onClose();
  };

  const handleSubmit = () => {
    // 验证输入
    if (!username.trim()) {
      Message.error('请输入用户名');
      return;
    }
    if (!password.trim()) {
      Message.error('请输入密码');
      return;
    }
    if (password.length < 6) {
      Message.error('密码长度至少6位');
      return;
    }

    setLoading(true);
    
    // 临时方案：不调用登录接口，直接保存用户名和密码到本地
    console.log('========================================');
    console.log('🔑 登录成功（临时方案 - 本地存储）');
    console.log('用户名:', username);
    console.log('密码:', password);
    console.log('========================================');
    
    // 保存登录状态（包含用户名和密码，用于后续调用 GET_SESSION_ID 接口）
    dispatch(login({ 
      username: username.trim(), 
      password,
      token: `token_${Date.now()}`,
      // apiKey 暂时不设置，等开启新会话时从 GET_SESSION_ID 接口获取
    }));

    Message.success('登录成功');
    setLoading(false);
    handleClose();
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      className={styles.loginModal}
      style={isMobile() ? { width: '90%', maxWidth: '90vw' } : { width: 480 }}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.title}>欢迎来到 AbsoluteMind</h2>
          <p className={styles.subtitle}>输入用户名和密码即可登录或注册</p>
        </div>

        <div className={styles.form}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>用户名</label>
            <Input
              placeholder="请输入用户名"
              value={username}
              onChange={(value) => setUsername(value)}
              onPressEnter={handleSubmit}
              className={styles.input}
              size="large"
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>密码</label>
            <Input.Password
              placeholder="请输入密码（至少6位）"
              value={password}
              onChange={(value) => setPassword(value)}
              onPressEnter={handleSubmit}
              className={styles.input}
              size="large"
            />
          </div>

          <Button
            type="primary"
            long
            loading={loading}
            onClick={handleSubmit}
            className={styles.submitButton}
            size="large"
          >
            {loading ? '处理中...' : '登录 / 注册'}
          </Button>

          <p className={styles.tip}>
            首次使用将自动为您创建账号
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default LoginModal;
